import apiClient from "./apiService";
import { Review, ReviewStats, Store, ProductStats, Rating } from "@types";

interface ApiOptionalFields {
  advantages?: string;
  disadvantages?: string;
  question_link?: string;
  answered_datetime?: string;
  recommend?: boolean;
}

interface ApiReview {
  id: number;
  feedback_id: string;
  type: string;
  marketplace_type: string;
  product_name: string;
  date: string;
  text: string;
  rating: number;
  customer_name: string;
  optional_fields: ApiOptionalFields;
  photos?: { url: string }[];
  videos?: { url: string }[];
  source?: string;
  pros: string;
  cons: string;
  product_link?: string;
  vendor_answer?: string;
}

interface ApiResponseMeta {
  page: number;
  size: number;
  total_pages: number;
  total_len: number;
  has_next: boolean;
  prev: string | null;
  next: string | null;
}

interface ApiResponse {
  feedbacks: ApiReview[];
  meta: ApiResponseMeta;
}

interface FeedbacksResponse {
  content: Review[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface FeedbacksParams {
  // Query parameters
  page?: number;
  size?: number;
  product_name?: string;
  product_id__in?: string;
  rating__gte?: number;
  rating__lte?: number;
  date__gte?: string;
  date__lte?: string;
  has_photos?: boolean;
  has_videos?: boolean;
  has_pros_cons?: boolean;
  search?: string;

  // Request body parameters
  type__in?: string; // feedback или question
  rating__in?: number[];
  integration_id__in?: number[];
  order_by?: string[];
}

export const reviewsService = {
  /**
   * Преобразует данные отзыва от API в формат приложения
   * @param apiReview Данные отзыва от API
   * @returns Отзыв в формате приложения
   */
  mapApiReviewToAppReview: (apiReview: ApiReview): Review => {
    // Преобразуем marketplace_type в enum Store
    let store: Store | undefined;
    if (apiReview.marketplace_type) {
      switch (apiReview.marketplace_type.toLowerCase()) {
        case "ozon":
          store = Store.OZON;
          break;
        case "wildberries":
          store = Store.WILDBERRIES;
          break;
        case "yandex":
          store = Store.YANDEX;
          break;
        case "sbermarket":
          store = Store.SBERMARKET;
          break;
        case "sber":
          store = Store.SBERMARKET;
          break;
        default:
          store = undefined;
      }
    }

    let answer = undefined;
    const vendorAnswer = apiReview.vendor_answer;
    const answerDate =
      apiReview.optional_fields?.answered_datetime || apiReview.date;

    if (vendorAnswer) {
      answer = {
        author: "Продавец", // Значение по умолчанию
        text: vendorAnswer,
        date: answerDate,
      };
    }

    // Преобразуем строковые массивы фото и видео в объекты
    const photos = apiReview.photos
      ? apiReview.photos.map((photo) => ({
          url: photo.url,
          width: 0,
          height: 0,
        }))
      : [];

    const videos = apiReview.videos
      ? apiReview.videos.map((video) => ({
          url: video.url,
          thumbnailUrl: undefined,
          preview_url: undefined,
          short_video_preview_url: undefined,
          width: 0,
          height: 0,
        }))
      : [];

    return {
      id: apiReview.feedback_id,
      author: apiReview.customer_name || "Анонимный пользователь",
      productName: apiReview.product_name,
      rating: apiReview.rating || 0,
      comment: apiReview.text || "",
      date: apiReview.date,
      store: store,
      productLink: apiReview.product_link || undefined,
      answer: answer || undefined,
      advantages: apiReview.pros || undefined,
      disadvantages: apiReview.cons || undefined,
      recommends: apiReview.optional_fields?.recommend,
      photos: photos,
      videos: videos,
    };
  },

  /**
   * Получение отзывов о продукте
   * @param params Параметры запроса (страница, размер страницы, идентификатор продукта и другие фильтры)
   * @returns Promise с данными отзывов
   */
  getFeedbacks: async (params: FeedbacksParams): Promise<FeedbacksResponse> => {
    // Create a copy of params to avoid modifying the original
    const apiParams = { ...params };

    const response = await apiClient.get<ApiResponse>("widget/feedbacks", {
      params: apiParams,
    });

    console.log("feedbacks response", response.data);

    // Преобразуем данные от API в формат приложения
    const mappedContent = response.data.feedbacks.map(
      reviewsService.mapApiReviewToAppReview
    );

    return {
      content: mappedContent,
      totalElements: response.data.meta.total_len,
      totalPages: response.data.meta.total_pages,
      size: response.data.meta.size,
      number: response.data.meta.page,
    };
  },

  /**
   * Получает статистику для продукта из API
   * @param productName Название продукта
   * @returns Объект со статистикой отзывов
   */
  getProductStats: async (productName: string): Promise<ReviewStats> => {
    const response = await apiClient.get<ProductStats>(
      `widget/product/${productName}/stats`
    );

    // Используем total_stats для суммирования отзывов по рейтингам
    // Конвертируем из Record<string, number> в Record<Rating, number>
    const ratingDistribution: Record<Rating, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };

    // Обрабатываем данные статистики
    const totalStatsEntries = Object.entries(response.data.total_stats || {});
    let totalReviews = 0;

    // Пытаемся распознать рейтинги в ключах статистики
    for (const [key, count] of totalStatsEntries) {
      // Пытаемся извлечь рейтинг из ключа (может быть число или строка с числом)
      const ratingMatch = key.match(/(\d+)/);
      const rating = ratingMatch ? parseInt(ratingMatch[1], 10) : NaN;

      // Проверяем, что рейтинг - это число от 1 до 5
      if (!isNaN(rating) && rating >= 1 && rating <= 5) {
        ratingDistribution[rating as Rating] = count;
        totalReviews += count;
      }
    }

    // Если нет данных о распределении рейтингов или общее количество отзывов равно 0,
    // пытаемся получить значения из других свойств ответа
    if (totalReviews === 0) {
      // Можно попробовать использовать widget_stats или marketplace_stats
      const widgetStatsEntries = Object.entries(
        response.data.widget_stats || {}
      );
      for (const [key, count] of widgetStatsEntries) {
        const ratingMatch = key.match(/(\d+)/);
        const rating = ratingMatch ? parseInt(ratingMatch[1], 10) : NaN;

        if (!isNaN(rating) && rating >= 1 && rating <= 5) {
          ratingDistribution[rating as Rating] = count;
          totalReviews += count;
        }
      }
    }

    const result = {
      averageRating: response.data.average_rating || 0,
      totalReviews: totalReviews,
      ratingDistribution: ratingDistribution,
    };

    return result;
  },

  /**
   * Получение только отзывов о продукте
   * @param params Параметры запроса (страница, размер страницы, идентификатор продукта и другие фильтры)
   * @returns Promise с данными отзывов
   */
  getReviews: async (
    params: Omit<FeedbacksParams, "type__in">
  ): Promise<FeedbacksResponse> => {
    return reviewsService.getFeedbacks({
      ...params,
      type__in: "feedback",
    });
  },

  /**
   * Получение только вопросов о продукте
   * @param params Параметры запроса (страница, размер страницы, идентификатор продукта и другие фильтры)
   * @returns Promise с данными вопросов
   */
  getQuestions: async (
    params: Omit<FeedbacksParams, "type__in">
  ): Promise<FeedbacksResponse> => {
    return reviewsService.getFeedbacks({
      ...params,
      type__in: "question",
    });
  },
};

export default reviewsService;
